import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Loader2, Clock, Mic } from "lucide-react";
import { img, fmtDate, setMeta, ListenOn, AudioPlayer, PodcastCTA, useFeedLink, clean } from "@/components/PodcastParts";

function EpisodeCard({ ep, i }) {
  const desc = clean(ep.description);
  const short = desc.length > 220 ? `${desc.slice(0, 220).trim()}…` : desc;
  return (
    <article className="grid gap-4 rounded-2xl border border-border bg-white p-4 sm:grid-cols-[140px_1fr] sm:p-5" data-testid={`episode-card-${i}`}>
      <Link to={`/podcast/${ep.slug}`}><img src={img(ep.image)} alt={ep.title} className="aspect-square w-full rounded-xl object-cover sm:w-[140px]" loading="lazy" /></Link>
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2 text-xs font-semibold uppercase tracking-wider text-sam-crimson">
          {ep.episode && <span>Episode {ep.episode}</span>}<span className="text-sam-slate">{fmtDate(ep.pub_date)}</span>
          {ep.duration && <span className="inline-flex items-center gap-1 text-sam-slate"><Clock className="h-3 w-3" />{ep.duration}</span>}
        </div>
        <Link to={`/podcast/${ep.slug}`} className="mt-1 block font-display text-lg sm:text-xl font-bold text-sam-navy hover:underline" data-testid={`episode-link-${i}`}>{ep.title}</Link>
        <p className="mt-1 text-sm text-sam-slate">{short}</p>
        <div className="mt-3"><AudioPlayer ep={ep} testid={`episode-audio-${i}`} /></div>
      </div>
    </article>
  );
}

export default function Podcast() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useFeedLink(d?.show?.feed_url);
  useEffect(() => {
    api.get("/podcast").then(({ data }) => {
      setD(data);
      const title = "Saving America Money Podcast | Hosted by Troy W Berkley";
      document.title = title;
      const sd = clean(data.show.description);
      setMeta("description", sd.slice(0, 160));
      setMeta("og:title", title, "property");
      setMeta("og:description", sd.slice(0, 200), "property");
      setMeta("og:image", img(data.show.image), "property");
    }).catch(() => setErr("The podcast couldn't load right now. Please try again shortly."));
  }, []);
  if (err) return <div className="mx-auto max-w-3xl px-6 py-24 text-center text-sam-slate" data-testid="podcast-error">{err}</div>;
  if (!d) return <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-sam-navy" /></div>;
  const { show, episodes } = d;
  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-10 sm:py-14" data-testid="podcast-page">
      <section className="grid gap-8 md:grid-cols-[300px_1fr] md:items-start">
        {show.image && <img src={img(show.image)} alt="Saving America Money Podcast cover" className="w-full max-w-[300px] rounded-2xl shadow-xl" data-testid="podcast-cover" />}
        <div>
          <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-sam-crimson"><Mic className="h-4 w-4" />Podcast</div>
          <h1 className="mt-2 font-display text-4xl sm:text-5xl lg:text-6xl font-black text-sam-navy" data-testid="podcast-title">{show.title}</h1>
          <p className="mt-2 font-semibold text-sam-navy" data-testid="podcast-host">Hosted by {show.host}</p>
          <div className="mt-4 space-y-3 text-sm sm:text-base leading-relaxed text-sam-slate" data-testid="podcast-description">
            {clean(show.description).split(/\n\s*\n/).map((p, i) => <p key={i}>{p}</p>)}
          </div>
          <div className="mt-6"><ListenOn links={show.links} /></div>
        </div>
      </section>
      <section className="mt-12">
        <h2 className="mb-4 font-display text-base md:text-lg font-bold uppercase tracking-wider text-sam-navy">Episodes ({episodes.length})</h2>
        <div className="space-y-4" data-testid="episode-list">
          {episodes.map((ep, i) => <EpisodeCard key={ep.guid} ep={ep} i={i} />)}
          {!episodes.length && <p className="text-sam-slate" data-testid="episode-empty">The first episode is on its way.</p>}
        </div>
      </section>
      <div className="mt-12"><PodcastCTA /></div>
    </div>
  );
}
